import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Navigation, ArrowUp, Home, CalendarDays, ClipboardList, X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { rutaInicio } from "../lib/permisos";

/**
 * Botón flotante de navegación rápida.
 *  - Lateral derecho, esquina inferior (sobre el área del HelpDrawer, sin chocar)
 *  - Se contrae automáticamente al hacer scroll hacia abajo (anti-distracción)
 *  - Se expande con tap del usuario
 *  - Se oculta totalmente si el usuario lo desactiva desde /perfil
 *
 * Preferencia guardada en localStorage:
 *   - "ui.fab.enabled" — "1" o "0" (default "1" = activado)
 */
const LS_KEY = "ui.fab.enabled";

export function QuickNavFab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [habilitado, setHabilitado] = useState(() => readPref());
  const [visible, setVisible] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const lastScrollY = useRef(0);
  const hideTimer = useRef<number | null>(null);

  // Escuchar cambio de preferencia (cuando lo togglea desde /perfil)
  useEffect(() => {
    const handler = () => setHabilitado(readPref());
    window.addEventListener("storage", handler);
    window.addEventListener("fab-pref-change", handler as EventListener);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("fab-pref-change", handler as EventListener);
    };
  }, []);

  // Auto-hide en scroll-down, mostrar en scroll-up
  useEffect(() => {
    if (!habilitado) return;
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollY.current;
      if (Math.abs(delta) < 8) return; // ignorar micro-scrolls
      if (delta > 0 && y > 200) {
        // scroll hacia abajo → ocultar
        setVisible(false);
        setAbierto(false);
      } else {
        // scroll hacia arriba → mostrar
        setVisible(true);
      }
      lastScrollY.current = y;

      // Re-aparecer tras 2s de inactividad
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setVisible(true), 2000);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, [habilitado]);

  // No mostrar si: deshabilitado o no logueado
  if (!habilitado || !user) return null;

  const irAlTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setAbierto(false);
  };

  const irA = (ruta: string) => {
    navigate(ruta);
    setAbierto(false);
  };

  const home = rutaInicio(user);

  return (
    <div
      className={`fixed z-40 transition-all duration-300 ${
        visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12 pointer-events-none"
      }`}
      style={{ right: "1rem", bottom: "5.5rem" }}
      aria-label="Navegación rápida"
    >
      {abierto && (
        <div className="flex flex-col gap-2 mb-2 items-end animate-fade-in">
          <FabAction onClick={irAlTop} label="Ir arriba" color="bg-ink-700">
            <ArrowUp className="size-4" />
          </FabAction>
          <FabAction onClick={() => irA(home)} label="Inicio" color="bg-emerald-600">
            <Home className="size-4" />
          </FabAction>
          <FabAction onClick={() => irA("/calendario")} label="Calendario" color="bg-indigo-600">
            <CalendarDays className="size-4" />
          </FabAction>
          <FabAction onClick={() => irA("/reservas")} label="Reservas" color="bg-amber-600">
            <ClipboardList className="size-4" />
          </FabAction>
        </div>
      )}

      <button
        onClick={() => setAbierto((v) => !v)}
        className={`size-11 rounded-full shadow-lg text-white transition-all flex items-center justify-center ${
          abierto
            ? "bg-ink-700 rotate-45"
            : "bg-gradient-to-br from-brand-500 to-brand-700 hover:scale-105"
        }`}
        title="Navegación rápida"
        aria-expanded={abierto}
      >
        {abierto ? <X className="size-5" /> : <Navigation className="size-5" />}
      </button>
    </div>
  );
}

function FabAction({
  onClick, label, color, children,
}: {
  onClick: () => void; label: string; color: string; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`group flex items-center gap-2 ${color} text-white rounded-full shadow-md hover:shadow-lg transition-all pl-3 pr-3.5 py-1.5 text-sm font-medium`}
    >
      <span className="text-[11px] opacity-0 group-hover:opacity-100 transition-opacity max-w-0 group-hover:max-w-[100px] overflow-hidden whitespace-nowrap">
        {label}
      </span>
      <span className="size-7 rounded-full flex items-center justify-center bg-white/15">
        {children}
      </span>
    </button>
  );
}

// ────────────────────────────────────────────────────────────
// Helpers para preferencia
// ────────────────────────────────────────────────────────────
function readPref(): boolean {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw === null) return true; // default = activado
    return raw === "1";
  } catch { return true; }
}

export function setFabHabilitado(habilitado: boolean): void {
  try {
    localStorage.setItem(LS_KEY, habilitado ? "1" : "0");
    window.dispatchEvent(new CustomEvent("fab-pref-change"));
  } catch {}
}

export function getFabHabilitado(): boolean {
  return readPref();
}
